import React from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import TitleBasic, {titleBasicColors, titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

const BASE_CLASS = 'mt-ListBasic'
const LIST_CLASS = `${BASE_CLASS}-list`
const LIST_ITEM_CLASS = `${LIST_CLASS}Item`
const LIST_ITEM_CONTENT_CLASS = `${LIST_ITEM_CLASS}Content`
const LIST_ITEM_KEY_CLASS = `${LIST_ITEM_CLASS}Key`
const LIST_ITEM_VALUE_CLASS = `${LIST_ITEM_CLASS}Value`

const ROLE = 'list-basic-item'
const LIST_BASIC_ITEM_KEY_TEST_ID = `${ROLE}-key`
const LIST_BASIC_ITEM_VALUE_TEST_ID = `${ROLE}-value`

const DEFAULT_VALUE = '-'

const LIST_TYPES = {
  DISC: 'disc',
  ICON: 'icon'
}

const LIST_COLUMNS = {
  S: 1,
  M: 2,
  L: 3
}

const ITEMS_BOTTOM_MARGINS = {
  M: 'M',
  L: 'L'
}

const DEFAULT_LIST_TYPE = LIST_TYPES.DISC

const ITEMS_COLORS = {
  CURRENT_COLOR: 'currentColor',
  ACCENT: 'accent',
  SUCCESS: 'success'
}

const DEFAULT_TITLE_SIZE = titleBasicSizes.xs

const ListBasic = ({
  avoidItemColumnBreak = false,
  icon = {},
  isMarginlessList = false,
  // TO-DO: deprecate this prop (use maxColumns)
  isSingleColumn = false,
  items,
  itemsColor,
  itemsBottomMargins = ITEMS_BOTTOM_MARGINS.L,
  maxColumns,
  title,
  titleColor,
  titleDescription,
  titleSize = DEFAULT_TITLE_SIZE,
  type = DEFAULT_LIST_TYPE
}) => {
  const isListTypeIcon = type === LIST_TYPES.ICON
  const hasMaxColumns = Boolean(maxColumns)
  const listClass = cx(LIST_CLASS, {
    'is-iconList': isListTypeIcon,
    // TO-DO: remove this class when isSingleColumn prop was deprecated
    [`${LIST_CLASS}--cols${LIST_COLUMNS.S}`]: isSingleColumn,
    [`${LIST_CLASS}--cols${maxColumns}`]: hasMaxColumns,
    [`${LIST_CLASS}--isMarginless`]: isMarginlessList
  })

  const listItemClass = cx(LIST_ITEM_CLASS, `${LIST_ITEM_CLASS}--marginBottom${itemsBottomMargins}`, {
    [`${LIST_ITEM_CLASS}-${type}`]: type,
    'is-currentColor': itemsColor === ITEMS_COLORS.CURRENT_COLOR,
    'is-accent': itemsColor === ITEMS_COLORS.ACCENT,
    'is-success': itemsColor === ITEMS_COLORS.SUCCESS
  })

  const listItemContentClass = cx(LIST_ITEM_CONTENT_CLASS, {
    [`${LIST_ITEM_CONTENT_CLASS}--avoidColumnBreak`]: avoidItemColumnBreak
  })

  const {color: iconColor, size: iconSize, type: CommonIcon} = icon

  const Title =
    typeof title === 'string'
      ? () => (
          <TitleBasic
            as={titleBasicTags.h5}
            size={titleSize}
            title={title}
            {...(titleColor && {color: titleColor})}
            {...(titleDescription && {description: titleDescription})}
          />
        )
      : title

  return (
    <>
      {title && <Title />}
      <ul className={listClass}>
        {items.map((item, i) => {
          const {icon: SingleIcon, key, value, title: itemTitle, titleSize: itemTitleSize = titleBasicSizes.xxs} = item
          const ListIcon = SingleIcon || CommonIcon
          const iconExist = !!ListIcon
          return (
            <li key={i} className={listItemClass}>
              {isListTypeIcon && iconExist && <ListIcon size={iconSize} color={iconColor} />}
              <div>
                <div className={listItemContentClass}>
                  {itemTitle ? <TitleBasic as={titleBasicTags.h6} size={itemTitleSize} title={itemTitle} /> : null}
                  {key ? (
                    <>
                      <strong className={LIST_ITEM_KEY_CLASS} data-testid={LIST_BASIC_ITEM_KEY_TEST_ID}>
                        {key}
                      </strong>
                      <span>: </span>
                      <span className={LIST_ITEM_VALUE_CLASS} data-testid={LIST_BASIC_ITEM_VALUE_TEST_ID}>
                        {htmlStringToReactElement(value || DEFAULT_VALUE)}
                      </span>
                    </>
                  ) : (
                    <span>{htmlStringToReactElement(value)}</span>
                  )}
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </>
  )
}

ListBasic.displayName = 'ListBasic'
ListBasic.propTypes = {
  avoidItemColumnBreak: PropTypes.bool,
  icon: PropTypes.shape({
    color: PropTypes.string,
    size: PropTypes.string,
    type: PropTypes.func
  }),
  isMarginlessList: PropTypes.bool,
  // TO-DO: deprecate this prop (use maxColumns)
  isSingleColumn: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.func,
      key: PropTypes.string,
      title: PropTypes.string,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  itemsBottomMargins: PropTypes.oneOf(Object.values(ITEMS_BOTTOM_MARGINS)),
  itemsColor: PropTypes.oneOf(Object.values(ITEMS_COLORS)),
  maxColumns: PropTypes.oneOf(Object.values(LIST_COLUMNS)),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  titleColor: PropTypes.oneOf(Object.values(titleBasicColors)),
  titleDescription: PropTypes.string,
  titleSize: PropTypes.oneOf(Object.values(titleBasicSizes)),
  type: PropTypes.oneOf(Object.values(LIST_TYPES))
}

export {
  ITEMS_BOTTOM_MARGINS as listItemsBottomMargins,
  ITEMS_COLORS as listItemsColors,
  LIST_COLUMNS as listColumns,
  LIST_TYPES as listTypes,
  titleBasicSizes as listTitleSizes
}

export default ListBasic
